// src/Dashboard/pages/user/FicheAdherent/hooks/useFicheAdherentDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";

export function useFicheAdherentDashboard(userId) {
  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const normalizeList = useCallback((data) => {
    return Array.isArray(data)
      ? [...data].sort((a, b) =>
          `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
            `${b.nom || ""} ${b.prenom || ""}`,
            "fr"
          )
        )
      : [];
  }, []);

  const loadAdherents = useCallback(
    async ({ silent = false } = {}) => {
      if (!userId) {
        setAdherents([]);
        setSelectedAdherentId(null);
        setLoading(false);
        return;
      }

      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const data = await getAdherentsByUser(userId);
        const next = normalizeList(data);

        setAdherents(next);

        setSelectedAdherentId((currentId) => {
          if (!next.length) return null;
          if (!currentId) return next[0].id;
          const stillExists = next.some((adherent) => adherent.id === currentId);
          return stillExists ? currentId : next[0].id;
        });
      } catch (error) {
        console.error("[useFicheAdherentDashboard] load error:", error);
        toast.error("Impossible de charger vos adhérents.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userId, normalizeList]
  );

  useEffect(() => {
    loadAdherents();
  }, [loadAdherents]);

  const selectedAdherent = useMemo(() => {
    return (
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId)
      ) || null
    );
  }, [adherents, selectedAdherentId]);

  const refreshAdherents = useCallback(() => {
    return loadAdherents({ silent: true });
  }, [loadAdherents]);

  return {
    adherents,
    selectedAdherent,
    selectedAdherentId,
    setSelectedAdherentId,
    loading,
    refreshing,
    refreshAdherents,
  };
}